// frontend/src/components/Timer.js
import React, { memo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
} from "..";
import { logout, getMe } from "../../actions/user";
import { makeStyles } from "@material-ui/core/styles";
import { resetTimer } from "../../actions/timer";

const INACTIVITY_TIMEOUT = 25 * 60 * 1000; // 25 minutes (fallback)
const WARNING_DURATION = 300; // 5 minutes in seconds

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 400,
  },
  timerBox: {
    marginTop: theme.spacing(2),
    textAlign: "center",
  },
  timerText: {
    fontWeight: "bold",
    color: theme.palette.error.main,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1),
  },
  progress: {
    marginTop: theme.spacing(2),
  },
}));

function Timer() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const sessionExpiresAt = useSelector((state) => state.timer.sessionExpiresAt);

  const [open, setOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_DURATION);
  const [isExtending, setIsExtending] = useState(false);

  const onLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // Helper: read expiry from X-Session-Expires cookie (ms since epoch)
  const readSessionCookie = () => {
    try {
      const cookies = document.cookie.split(";");
      const sessionExpiryCookie = cookies.find((cookie) =>
        cookie.trim().startsWith("X-Session-Expires=")
      );
      if (!sessionExpiryCookie) return null;

      const raw = sessionExpiryCookie.split("=")[1];
      const parsed = parseInt(raw, 10);
      return isNaN(parsed) ? null : parsed;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Don’t do anything while we are actively extending the session
      if (isExtending) return;

      let effectiveExpiresAt = sessionExpiresAt || readSessionCookie();

      // Fallback if nothing known (should be rare)
      if (!effectiveExpiresAt) {
        effectiveExpiresAt = Date.now() + INACTIVITY_TIMEOUT;
        dispatch(resetTimer(effectiveExpiresAt));
      }

      const now = Date.now();
      const secondsLeft = Math.floor((effectiveExpiresAt - now) / 1000);

      // Auto logout when user did not click Continue and session has expired
      if (secondsLeft <= 0) {
        onLogout();
        return;
      }

      // Within warning window
      if (secondsLeft <= WARNING_DURATION) {
        // First time entering warning window: open modal and set remaining time
        if (!open) {
          setOpen(true);
          setTimeRemaining(secondsLeft);
        } else {
          // While open, keep updating countdown
          setTimeRemaining(secondsLeft);
        }
      } else {
        // More than WARNING_DURATION left -> make sure modal is closed
        if (open) {
          setOpen(false);
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [sessionExpiresAt, open, isExtending, dispatch, onLogout]);

  const handleContinue = async () => {
    setIsExtending(true); // block modal re-open while refreshing
    try {
      // This should refresh the server session and reset X-Session-Expires
      await dispatch(getMe());
      setOpen(false); // close modal after successful refresh
    } catch (error) {
      // If refresh fails, log user out
      onLogout();
      return;
    } finally {
      setIsExtending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressValue = Math.max(
    0,
    Math.min(100, (timeRemaining / WARNING_DURATION) * 100)
  );

  return (
    <Dialog open={open} onClose={() => {}} style={{ zIndex: 10000 }}>
      <DialogTitle>
        <Typography variant="h6" color="error">
          {t("Session Expiring")}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          {t(
            "Your session will expire soon. To stay logged in, click continue."
          )}
        </DialogContentText>

        <Box className={classes.timerBox}>
          <Typography className={classes.timerText}>
            {t("Time Remaining: {{time}}", {
              time: formatTime(timeRemaining),
            })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            className={classes.progress}
            color="secondary"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleContinue}
          color="secondary"
          variant="contained"
          data-test-id="continue-dialog-continue-button"
        >
          {t("Continue Session")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(Timer);
